import { useEffect, useState } from "react";

import {
  Table, TableHead, TableRow, TableCell,
  TableBody, Paper, Typography, CircularProgress,
  Snackbar,
  Alert,
  TableContainer,
  Box,
  TextField,
  Autocomplete,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Button,
  useMediaQuery,
  useTheme,
  IconButton,
  Collapse
} from "@mui/material";
import FacturacionService from "../../services/FacturacionService";
import ConfigUtils from "../../utils/Config";
import FacturarCitaDialog from "./facturarCita/FacturarCitaDialog";
import { useNavigate } from "react-router-dom";
import * as constantes from "../../common/Constantes";
import ILog from "../../types/Log";
import LogService from "../../services/LogService";
import IAlert from "../../types/Alert";
import TablePagination from "../utilities/TablePagination";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import EstadoFacturacionIcon, { EstadoFacturacion } from "./common/EstadoFacturacionIcon";
import FilterListIcon from '@mui/icons-material/FilterList';
import moment from "moment";

const locale = constantes.locale;

export interface INoFacturable {
  _id: string;
  fechaServicio: string | null;
  tipo: string;
  pacienteId: string | null;
  paciente: string | null;
  servicioId: string;
  motivo?: string;
}

const ServiciosNoFacturables = () => {
  const history = useNavigate();
  const [alert, setAlert] = useState<IAlert>({ open: false, severity: "info", msg: "" });
  const [registros, setRegistros] = useState<INoFacturable[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalFacturarCita, setModalFacturarCita] = useState(false);
  const [selectedServicio, setSelectedServicio] = useState<INoFacturable | null>(null);

  const [filterStart, setFilterStart] = useState<Date | null>(null);
  const [filterEnd, setFilterEnd] = useState<Date | null>(null);
  const [filterPaciente, setFilterPaciente] = useState<string | null>(null);
  const [filterTipo, setFilterTipo] = useState("");

  const [page, setPage] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [filterStart, filterEnd, filterPaciente, filterTipo]);

  const userId = ConfigUtils.getUserId();

  const handleCloseAlert = () => setAlert({ open: false, severity: "info", msg: "" });

  const load = async () => {
    try {
      const response = await FacturacionService.getServiciosNoFacturables(userId);
      setRegistros(response.data || []);
    }  catch (e: any) {
      if (e.response?.status === 401) {
        localStorage.clear();
        history(constantes.R_HOME);
      } else {
        const error: ILog = {
          method: "ServiciosNoFacturables.Component.getServiciosNoFacturables",
          level: "error",
          message: e.message,
          meta: e.response,
        };
        LogService.logError(error);
        setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <CircularProgress />;

  const filteredRegistros = registros.filter(r => {
    const fecha = r.fechaServicio ? moment(r.fechaServicio) : null;
    const inicio = filterStart ? moment(filterStart).startOf('day') : null;
    const fin = filterEnd ? moment(filterEnd).endOf('day') : null;

    if (inicio && (!fecha || fecha.isBefore(inicio))) return false;
    if (fin && (!fecha || fecha.isAfter(fin))) return false;

    if (filterPaciente && (!r.paciente || r.paciente.toLowerCase().indexOf(filterPaciente.toLowerCase()) === -1)) return false;
    if (filterTipo && filterTipo !== "" && r.tipo !== filterTipo) return false;
    return true;
  });

  const pacientesUnicos = Array.from(
    new Set(registros.map(r => r.paciente).filter(p => p !== null))
  ).sort((a, b) => a!.localeCompare(b!, "es", { sensitivity: "base" }));

  const tipos = Array.from(
    new Set(registros.map(r => r.tipo).filter(p => p !== null))
  ).map(p => p.charAt(0).toUpperCase() + p.slice(1));

  const registrosPaginados = filteredRegistros.slice(
    page * constantes.rowsPerPage,
    page * constantes.rowsPerPage + constantes.rowsPerPage
  );

  const inputStyle = {
    "& .MuiInputBase-root": {
      height: 40,
      minHeight: 40,
      fontSize: 13,
      "& .MuiInputBase-input": {
        padding: "8px 12px",
        height: 24,
        boxSizing: "border-box",
        fontSize: 13,
      },
    },
    "& .MuiInputLabel-root": {
      fontSize: 13,
    },
    "& .MuiAutocomplete-inputRoot": {
      minHeight: 40,
    },
  };

  return (
    <>
      {registros.length === 0 ? (
        <Typography variant="body1">
          No hay servicios marcados como no facturables.
        </Typography>
      ) : (
        <Paper sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.06)" }}>

          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                letterSpacing: 0.5,
                flex: 1,
                minWidth: 0,
                fontSize: { xs: "1.25rem", sm: "1.75rem" },
                whiteSpace: { xs: "nowrap", sm: "normal" },
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {isDesktop ? "Servicios marcados como no facturables" : "No facturables"}
            </Typography>
            {!isDesktop && (
              <IconButton onClick={() => setShowFilters(prev => !prev)}>
                <FilterListIcon />
              </IconButton>
            )}
          </Box>

          {/* FILTROS */}
          <Collapse in={isDesktop || showFilters} timeout="auto" unmountOnExit>
          <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={locale}>
            <Box display="flex" gap={2} mb={2} flexWrap="wrap">
              <DatePicker
                label="Desde"
                value={filterStart}
                onChange={setFilterStart}
                renderInput={(params) => (
                  <TextField {...params} sx={{ width: { xs: "100%", sm: 140 }, ...inputStyle }} size="small" InputLabelProps={{ style: { fontSize: 13 } }} />
                )}
              />
              <DatePicker
                label="Hasta"
                value={filterEnd}
                onChange={setFilterEnd}
                renderInput={(params) => (
                  <TextField {...params} sx={{ width: { xs: "100%", sm: 140 }, ...inputStyle }} size="small" InputLabelProps={{ style: { fontSize: 13 } }} />
                )}
              />
              <Autocomplete
                disablePortal
                options={pacientesUnicos}
                value={filterPaciente}
                onChange={(_, newValue) => setFilterPaciente(newValue)}
                renderInput={(params) => <TextField {...params} label="Paciente" size="small" sx={{ ...inputStyle, minWidth: 140, width: "100%" }} InputLabelProps={{ style: { fontSize: 13 } }} />}
                    sx={{ flexGrow: 1, minWidth: 200 }}
              />
              <FormControl sx={{ width: { xs: "100%", sm: 100 } }} size="small">
                <InputLabel sx={{ fontSize: 13 }}>Tipo</InputLabel>
                <Select
                  value={filterTipo}
                  onChange={(e) => setFilterTipo(e.target.value)}
                  label="Tipo"
                  sx={{
                    "& .MuiSelect-select": {
                      fontSize: 13,
                    }
                  }}
                >
                  {tipos.map(t => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                sx={{ width: { xs: "100%", sm: "auto" } }}
                onClick={() => {
                  setFilterStart(null);
                  setFilterEnd(null);
                  setFilterPaciente(null);
                  setFilterTipo("");
                }}
              >
                Limpiar filtros
              </Button>
            </Box>
          </LocalizationProvider>
          </Collapse>

          {isMobile ? 
          <Box>
            {registrosPaginados.map((item) => (
              <Paper key={item._id} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
              
                {/* PACIENTE */}
                <Typography variant="body1" fontWeight={500}>
                  {item.paciente}
                </Typography>
            
                {/* TIPO */}
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  {item.tipo ? item.tipo.charAt(0).toUpperCase() + item.tipo.slice(1) : ""}
                </Typography>
            
                {/* FECHA */}
                <Box mt={1}>
                  {item.fechaServicio ? (
                    <>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {new Date(item.fechaServicio).toLocaleDateString("es-ES")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(item.fechaServicio).toLocaleTimeString("es-ES", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  )}
                </Box>
                
                {/* MOTIVO */}
                {item.motivo && (
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    {item.motivo}
                  </Typography>
                )}
        
                {/* ACCIONES */}
                <Box display="flex" justifyContent="flex-end" mt={1.5}>
                  {item.tipo === "Cita" && (
                    <EstadoFacturacionIcon
                      estado={EstadoFacturacion.NO_FACTURABLE}
                      onClick={() => {
                        setSelectedServicio(item);
                        setModalFacturarCita(true);
                      }}
                    />
                  )}
                </Box>
              </Paper>
            ))}
          </Box> : 
          <TableContainer>
            <Table size="small">
              <TableHead
                sx={{ 
                  backgroundColor: "grey.100",
                  "& .MuiTableCell-head": {
                    fontWeight: 600,
                    color: "grey.800",
                    fontSize: "0.95rem",
                  }
                }}
              >
                <TableRow>
                  <TableCell sx={{ width: { xs: 110, sm: 130 }, whiteSpace: "nowrap" }}>Fecha del servicio </TableCell>
                  <TableCell sx={{ width: "40%" }}>Paciente</TableCell>
                  <TableCell sx={{ width: "20%" }}>Tipo</TableCell>
                  <TableCell sx={{ width: "20%" }}>Motivo</TableCell>
                  <TableCell sx={{ width: 60 }} align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {registrosPaginados.map((item, index) => (
                  <TableRow 
                    key={item._id}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "white" : "grey.50",
                      "&:hover": {
                        backgroundColor: "grey.100"
                      }
                    }}
                  >
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {item.fechaServicio ? (
                      <Box display="flex" flexDirection="column">
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {new Date(item.fechaServicio).toLocaleDateString("es-ES")}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(item.fechaServicio).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                    <TableCell>{item.paciente}</TableCell>
                    <TableCell>{item.tipo ? item.tipo.charAt(0).toUpperCase() + item.tipo.slice(1) : ''}</TableCell>
                    <TableCell>{item.motivo}</TableCell>
                    <TableCell align="right">
                      {item.tipo === "Cita" && <EstadoFacturacionIcon
                        estado={EstadoFacturacion.NO_FACTURABLE}
                        onClick={() => {
                          setSelectedServicio(item);
                          setModalFacturarCita(true);
                        }}
                      />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>}

          {/* PAGINACIÓN */}
          <TablePagination
            count={filteredRegistros.length}
            page={page}
            rowsPerPage={constantes.rowsPerPage}
            onPageChange={setPage}
          />

        </Paper>
      )}

    {/* FACTURAR CITA */}
    {modalFacturarCita && 
        <FacturarCitaDialog 
            open={modalFacturarCita} 
            tipo="original"
            onClose={() => {setModalFacturarCita(false); setSelectedServicio(null)}} 
            citaId={selectedServicio?.servicioId!}
            pacienteId={selectedServicio?.pacienteId!}
            onServicioActualizado={(updated) => {
              if (updated.estado !== "no_facturable") {
                setRegistros(prev => prev.filter(r => r.servicioId !== updated._id));
                setAlert({ open: true, severity: "success", msg: "Cita facturada correctamente" });
              }
            }}
            onMotivoActualizado={(nuevoMotivo) => {
              setRegistros(prev => prev.map(r => r._id === selectedServicio?._id ? { ...r, motivo: nuevoMotivo } : r));
            }}
        />
    }

    <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
      <Alert variant="filled"  onClose={handleCloseAlert} severity={alert.severity} sx={{ width: "100%" }}>
        {alert.msg}
      </Alert>
    </Snackbar>
    </>
  );
};

export default ServiciosNoFacturables;